import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { GS1NotificationService } from "@gs1france/gs1-components-library";
import { catchError, lastValueFrom, Observable } from "rxjs";
import { GLNInterface, GlnCompanySearchInfo, CompanySearchResponse } from "src/app/models/gln.model";
import { SearchGLNRequest, SearchGLNResults, SearchedGLN } from "src/app/models/search.model";
import { UploadResult } from "src/app/models/upload-results.model";
import { environment } from '../../../../environments/environment';
import { FileService } from "../file-service/file-service.service";

@Injectable({
  providedIn: 'root'
})
export class GLNService {
  constructor(private http: HttpClient, private notifications: GS1NotificationService, private fileService: FileService) {}

  /**
   * Get the GLNs of the user's account.
   * @returns A promise that resolves to an array of GLNs.
   */
  async getAccountGLNs() {
    try {
      const glnsRaw = await lastValueFrom(this.http.get<GLNInterface[]>(`${environment.URL_Back}Gln`));
      return glnsRaw;
    } catch (err) {
      this.handleErrors(err, 'La récupération de vos GLN n\'a pas abouti.');
      throw err;
    }
  }

  /**
   * Get the GLN by its id.
   * @param {string} id - the id of the GLN
   * @returns {Promise<GLNInterface>} - the GLN
   */
  async getGlnById(id: string) {
    try {
      const gln = await lastValueFrom(this.http.get<GLNInterface>(`${environment.URL_Back}Gln/ById?id=${id}`));
      return gln;
    } catch (err) {
      this.handleErrors(err, 'La récupération des informations du GLN s\'est mal terminée');
      throw err;
    }
  }

  /**
   * Get the GLN by its number.
   * @param {string} glnNumber - the GLN number to get the GLN for.
   * @returns {Promise<GLNInterface>} - the GLN object.
   */
  async getGlnByNumber(glnNumber: string) {
    try {
      const gln = await lastValueFrom(this.http.get<GLNInterface>(`${environment.URL_Back}Gln/ByNumber?number=${glnNumber}`));
      return gln;
    } catch (err) {
      this.handleErrors(err, 'La récupération des informations du GLN s\'est mal terminée.');
      throw err;
    }
  }

  /**
   * Create a GLN and show a result notification.
   * @param {GLNInterface} gln - The GLN object to create.
   * @returns A GLN object.
   */
  async createGLN(gln: GLNInterface){
    try {
      let createdGln: GLNInterface | null = null;
      createdGln =  await lastValueFrom(this.http.post<GLNInterface>(`${environment.URL_Back}Gln`, gln));
      if(gln.status == "ACTIVE")
        this.handleSuccess('Votre code GLN a bien été créé.');
      else if(gln.status == "DRAFT")
        this.handleSuccess('Votre brouillon a bien été sauvegardé.');
      return createdGln;
    } catch (err) {
      this.handleErrors(err, 'La création de votre GLN n\'a pas abouti.');
      throw err
    }
  }

  /**
   * Saves the given GLN to the database and show a result notification.
   * @param {GLNInterface} gln - The GLN to save.
   * @returns None
   */
  async saveGLN(gln: GLNInterface) {
    try {
      let updatedGLN: GLNInterface | null = null;
      updatedGLN =  await lastValueFrom(this.http.put<GLNInterface>(`${environment.URL_Back}Gln`, gln));
      if (updatedGLN.status !== 'DRAFT') {
        this.handleSuccess('Vos modifications ont bien été sauvegardées.');
      } else {
        this.handleSuccess('Votre brouillon a bien été sauvegardé.');
      }
      return updatedGLN;
    } catch (err) {
      this.handleErrors(err, 'La sauvegarde de votre GLN n\'a pas abouti.');
      throw err
    }
  }

  /**
   * Gets the next GLN available for the given GCP.
   * @param {string} gcp - the GCP to get the next GLN for
   * @returns {Promise<number>} - the next GLN available for the given GCP
   */
  async getNextGLNForGCP(gcp: string) {
    try {
      const gln = await lastValueFrom(this.http.get<number>(`${environment.URL_Back}Gln/gcp?gcp=${gcp}`,{
        observe: 'response'
      }));
      if(gln.status == 204)
        this.notifications.addNotification('Aucun code GLN disponible avec ce GCP.', 'error');
      return gln.body;
    } catch (err) {
      if (err instanceof HttpErrorResponse && (err.status === 404 || err.status === 204)) {
        this.handleErrors(err, 'Aucun code GLN disponible avec ce GCP.')
      } else {
        this.handleErrors(err, 'La récupération du code GLN n\'a pas abouti.');
      }
      throw err;
    }
  }

    /**
   * Gets the Company names list based on how it startes
   * @param {string} cname - the company name
   * @returns {Promise<number>} - list of company names associated with the ID
   */
     async getCompanyNameListIncluding(cName: string):  Promise<GlnCompanySearchInfo[]>{
      try {
        const companies = await lastValueFrom(this.http.get<CompanySearchResponse>(`${environment.URL_Back}Company/byname?name=${cName}`,{
          observe: 'response'
        }));
        return companies.body?.companyList as GlnCompanySearchInfo[];
      } catch (err) {
        throw err;
      }
    }

  /**
   * Checks if the chosen Gln is valid and ready to use.
   * @param {string} gcp - the GCP to get the next GLN for
   * @param {string} gcp - the chosen suffix

   * @returns {Promise<number>} - the next GLN available for the given GCP
   */
   async checkGlnNumberVaidity(gcp: string, suffix: string) {
    try {
      const gln = await lastValueFrom(this.http.get<number>(`${environment.URL_Back}Gln/gcp?gcp=${gcp}&number=${suffix}`,{
        observe: 'response'
      }));
      return gln.body;
    } catch (err) {
      if (err instanceof HttpErrorResponse && (err.status === 404 || err.status === 204)) {
        this.handleErrors(err, 'Aucun code GLN disponible avec ce GCP.')
      } else {
        this.handleErrors(err, 'La récupération du code GLN n\'a pas abouti.');
      }
      throw err;
    }
  }
  /**
   * Get the template file to upload GLNs.
   * @returns None
   */
  async getIfsFile() {
    try {
      const url = `${environment.URL_Back}Gln/gettemplate`;
      window.open(url, '_self');
    } catch (err) {
      this.handleErrors(err, 'Le téléchargement du fichier n\'a pas abouti.');
      throw err;
    }
  }

  /**
   * Exports the Gln data to an excel file.
   * @returns None
   */
   async export(glns: GLNInterface[]) {
    try {
      const response =  await lastValueFrom(this.http.post(`${environment.URL_Back}Gln/export`, glns,{ responseType: 'blob'}));
      let filename: string = 'Gln Export.xlsx';
      this.fileService.downloadBlob(response, filename);
    } catch (err) {
      this.handleErrors(err, 'L\'exportation n\'a pas abouti, veuillez réessayer ultérieurement.');
      throw err;
    }
  }
  async exportSearch(data: SearchedGLN[]) {
    try {
       const response =  await lastValueFrom(this.http.post(`${environment.URL_Back}Gln/exportSearch`, data,{ responseType: 'blob'}));

      let filename: string = 'Export recherche.xlsx';
      this.fileService.downloadBlob(response, filename);
    } catch (err) {
      this.handleErrors(err, 'L\'exportation n\'a pas abouti, veuillez réessayer ultérieurement.');
      throw err;
    }
  }

  /**
   * Uploads a file containing GLNs to the server to create/update those and returns the response.
   * @param {any} fileToUpload - the file to upload
   * @returns {Promise<UploadResult>} - the response from the server
   */
  async uploadGLNs(fileToUpload: any) : Promise<UploadResult>{
    try {
      let file = fileToUpload[0];
      const formData = new FormData();
      formData.append('importFile', file);
      return  await lastValueFrom(this.http.post<UploadResult>(`${environment.URL_Back}Gln/import`, formData));
    } catch (err : any) {
      if(err.status === 500)
        this.handleErrors(err, 'L\'import du fichier n\'a pas abouti, veuillez réessayer ultérieurement.');

      throw err;

    }
  }

  /**
   * Searches for GLNs that match the given search criteria.
   * @param {SearchGLNRequest} request - The search criteria.
   * @returns An observable of the search results.
   */
  searchGLNGlobally(request: SearchGLNRequest): Observable<SearchGLNResults> {
    return this.http.post<SearchGLNResults>(`${environment.URL_Back}Gln/filter`, request);
  }

  /**
   * Adds a notification to the notifications array.
   * @param {string} msg - the message to add to the notifications array.
   * @returns None
   */
  handleSuccess(msg: string) {
    this.notifications.addNotification(msg, 'success');
  }

  /**
   * Handles errors by logging them to the console and adding a notification if a message is provided.
   * @param {any} err - the error object.
   * @param {string} [msg] - the message to display in the notification.
   * @returns None
   */
  handleErrors(err: any, msg?: string) {
    if (msg) {
      this.notifications.addNotification(msg, 'error');
    }
    throw err;
  }
}
